/**
 * Takım kurucu — kurumlar arası görev gücü.
 *
 * Bir TAKIM ile bir BİRİM farklı şeylerdir ve bu ayrım ürünün merkezinde:
 *
 *   • Birim, organizasyon şemasındaki gerçek yeridir. Hiyerarşiktir, bir
 *     personanın tek bir birimi olur ve belgeleri aşağı doğru miras kalır.
 *   • Takım, o şemayı KESEN bir kadrodur: Hukuk'tan bir müfettiş, Ar-Ge'den
 *     bir mühendis, Bütçe'den bir uzman. Aynı persona birden çok takımda
 *     yer alabilir; takımın kendi belgesi ya da hiyerarşisi yoktur.
 *
 * Bu yüzden takım formunda topoloji/tur sayısı ALANI YOK. Onlar tartışmanın
 * mekaniğidir, kadronun kimliği değil — sihirbazın 2. adımında, soruya göre
 * seçilirler. Kadroya sabitlemek, aynı ekibi farklı soru tipleri için
 * kullanmayı gereksizce zorlaştırıyordu.
 *
 * Kadro sırası ise anlamlıdır ve burada kalır: pozisyonel topolojilerde
 * konuşma düzenini, `diverse-poles`'ta ilk turdaki karşıt kutup eşleşmesini
 * belirler.
 */
import { useEffect, useMemo, useState } from "react";
import { Building2 } from "lucide-react";
import type {
  LibraryPersonaBrief,
  PersonaTeam,
  PersonaUnit,
} from "../../domain/personaLibrary";
import { unitPathLabel } from "../../domain/personaLibrary";
import { LibraryApiError, createTeam, updateTeam } from "../../transport/personaLibraryClient";
import { roleLabel } from "./PersonaComposer";
import {
  Btn,
  Chip,
  ErrorBanner,
  Field,
  Modal,
  PersonaAvatar,
  SectionTitle,
  TextArea,
  TextInput,
} from "./ui";

const UNASSIGNED_KEY = "__unassigned__";

interface UnitGroup {
  key: string;
  label: string;
  path: string;
  members: LibraryPersonaBrief[];
}

export function TeamEditor({
  open,
  team,
  personas,
  units,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** null ⇒ yeni takım. */
  team: PersonaTeam | null;
  personas: LibraryPersonaBrief[];
  units: PersonaUnit[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [mission, setMission] = useState("");
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(team?.name ?? "");
    setDescription(team?.description ?? "");
    setMission(team?.mission ?? "");
    setMemberIds(team?.members.map((m) => m.id) ?? []);
    setSearch("");
    setError(null);
  }, [open, team]);

  const byId = useMemo(() => new Map(personas.map((p) => [p.id, p])), [personas]);
  const unitById = useMemo(() => new Map(units.map((u) => [u.id, u])), [units]);

  /** Bir personanın birim etiketi — kadroda ve listede kimin nereden geldiği. */
  function unitLabelFor(p: LibraryPersonaBrief): string {
    if (!p.unitId) return "Atanmamış";
    return unitById.get(p.unitId)?.name ?? "Bilinmeyen birim";
  }

  const selected = memberIds
    .map((id) => byId.get(id))
    .filter((p): p is LibraryPersonaBrief => Boolean(p));

  /** Kadroda kaç FARKLI birim temsil ediliyor — görev gücünün asıl ölçüsü. */
  const representedUnits = new Set(selected.map((p) => p.unitId ?? UNASSIGNED_KEY));

  // Kütüphane panelini birime göre grupla: kurumlar arası bir kadro kurarken
  // "hangi birimden kimi aldım?" sorusu düz bir listede cevaplanamıyor.
  const groups: UnitGroup[] = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase("tr-TR");
    const pool = personas.filter((p) => {
      if (p.isArchived || memberIds.includes(p.id)) return false;
      if (!needle) return true;
      return (
        p.displayName.toLocaleLowerCase("tr-TR").includes(needle) ||
        (p.expertise ?? "").toLocaleLowerCase("tr-TR").includes(needle) ||
        unitLabelFor(p).toLocaleLowerCase("tr-TR").includes(needle)
      );
    });

    const buckets = new Map<string, UnitGroup>();
    for (const p of pool) {
      const key = p.unitId ?? UNASSIGNED_KEY;
      if (!buckets.has(key)) {
        buckets.set(key, {
          key,
          label: p.unitId ? (unitById.get(p.unitId)?.name ?? "Bilinmeyen") : "Atanmamış",
          path: p.unitId ? unitPathLabel(units, p.unitId) : "",
          members: [],
        });
      }
      buckets.get(key)?.members.push(p);
    }
    return [...buckets.values()].sort((a, b) => a.path.localeCompare(b.path, "tr"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personas, memberIds, search, units, unitById]);

  function toggle(id: string) {
    setMemberIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function move(index: number, delta: number) {
    setMemberIds((prev) => {
      const next = [...prev];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      const [item] = next.splice(index, 1);
      if (item !== undefined) next.splice(target, 0, item);
      return next;
    });
  }

  async function save() {
    setBusy(true);
    setError(null);
    const payload = {
      name: name.trim(),
      description: description.trim(),
      mission: mission.trim(),
      memberIds,
    };
    try {
      if (team) {
        await updateTeam(team.id, payload);
      } else {
        await createTeam(payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof LibraryApiError ? err.message : "Takım kaydedilemedi.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={team ? "Takımı Düzenle" : "Yeni Takım"}
      subtitle="Farklı kurum ve birimlerden uzmanları tek bir görev gücünde topla. Tartışma ayarları (topoloji, tur sayısı) soruya göre sihirbazda seçilir."
      width="max-w-4xl"
      footer={
        <>
          <Btn tone="ghost" onClick={onClose} disabled={busy}>
            Vazgeç
          </Btn>
          <Btn
            tone="primary"
            onClick={save}
            loading={busy}
            disabled={name.trim().length < 2 || memberIds.length === 0}
          >
            {team ? "Kaydet" : "Takımı Oluştur"}
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Takım Adı" required>
            <TextInput
              value={name}
              autoFocus
              maxLength={80}
              placeholder="ör. Tedarik Krizi Görev Gücü"
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <Field label="Açıklama">
            <TextInput
              value={description}
              maxLength={600}
              placeholder="Kısa bir tanım"
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>
        </div>

        <Field
          label="Takım Misyonu"
          hint="Opsiyonel. Bu kadronun hangi tür kararlar için kurulduğunu not et."
        >
          <TextArea
            rows={2}
            value={mission}
            maxLength={1200}
            onChange={(e) => setMission(e.target.value)}
          />
        </Field>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <SectionTitle
              title={`Kadro (${selected.length})`}
              hint="Sıra önemlidir: konuşma düzenini ve karşıt kutup eşleşmesini belirler."
            />
            {representedUnits.size > 1 && (
              <div className="flex items-center gap-1.5 rounded-lg border border-cyan-500/20 bg-cyan-500/[0.06] px-2.5 py-1.5">
                <Building2 className="h-3.5 w-3.5 shrink-0 text-cyan-300" aria-hidden />
                <span className="text-[11px] text-cyan-200">
                  {representedUnits.size} farklı birimden kurumlar arası kadro
                </span>
              </div>
            )}
            {selected.length === 0 ? (
              <p className="rounded-lg border border-dashed border-white/10 px-3 py-6 text-center text-[11.5px] text-slate-600">
                Sağdaki birimlerden persona ekle.
              </p>
            ) : (
              <ol className="space-y-1.5">
                {selected.map((p, i) => (
                  <li
                    key={p.id}
                    className="flex items-center gap-2 rounded-lg border border-white/[0.08] bg-white/[0.03] px-2.5 py-1.5"
                  >
                    <span className="w-4 shrink-0 text-center text-[11px] tabular-nums text-slate-500">
                      {i + 1}
                    </span>
                    <PersonaAvatar
                      url={p.avatarUrl}
                      name={p.displayName}
                      color={p.themeColor}
                      size={26}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[12px] text-slate-200">
                        {p.displayName}
                      </span>
                      <span
                        className="block truncate text-[10.5px] text-slate-500"
                        title={p.unitId ? unitPathLabel(units, p.unitId) : undefined}
                      >
                        {unitLabelFor(p)} · {p.expertise ?? roleLabel(p.role)}
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-0.5">
                      <button
                        type="button"
                        onClick={() => move(i, -1)}
                        disabled={i === 0}
                        className="rounded px-1 text-slate-500 hover:text-white disabled:opacity-30"
                        aria-label="Yukarı taşı"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => move(i, 1)}
                        disabled={i === selected.length - 1}
                        className="rounded px-1 text-slate-500 hover:text-white disabled:opacity-30"
                        aria-label="Aşağı taşı"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        onClick={() => toggle(p.id)}
                        className="rounded px-1 text-slate-500 hover:text-rose-400"
                        aria-label="Kadrodan çıkar"
                      >
                        ✕
                      </button>
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </div>

          <div className="space-y-2">
            <SectionTitle title="Kütüphane" hint="Birime göre gruplandı." />
            <TextInput
              value={search}
              placeholder="Ara: ad, unvan veya birim…"
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="max-h-80 space-y-3 overflow-y-auto pr-1">
              {groups.length === 0 ? (
                <p className="rounded-lg border border-dashed border-white/10 px-3 py-6 text-center text-[11.5px] text-slate-600">
                  {search
                    ? "Eşleşen persona yok."
                    : "Eklenebilecek başka persona yok."}
                </p>
              ) : (
                groups.map((group) => (
                  <div key={group.key} className="space-y-1">
                    <div
                      className="flex items-baseline gap-1.5 px-0.5"
                      title={group.path || undefined}
                    >
                      <span className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">
                        {group.label}
                      </span>
                      <span className="text-[10px] text-slate-700">
                        {group.members.length}
                      </span>
                    </div>
                    {group.members.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => toggle(p.id)}
                        className="flex w-full items-center gap-2 rounded-lg border border-white/[0.06] bg-white/[0.02] px-2.5 py-1.5 text-left transition-colors hover:border-violet-500/40"
                      >
                        <PersonaAvatar
                          url={p.avatarUrl}
                          name={p.displayName}
                          color={p.themeColor}
                          size={26}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[12px] text-slate-200">
                            {p.displayName}
                          </span>
                          <span className="block truncate text-[10.5px] text-slate-500">
                            {p.expertise ?? roleLabel(p.role)}
                          </span>
                        </span>
                        {p.stats.memoryCount > 0 && (
                          <Chip title="Hafıza notu sayısı">{p.stats.memoryCount}</Chip>
                        )}
                        <span className="shrink-0 text-[13px] text-violet-400">+</span>
                      </button>
                    ))}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
